import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";
import type {
  CatalogJobDiscoveryResult,
  CatalogJobListItem,
  CatalogJobUserStateSummary,
} from "@tabbio/types";
import { QUERY_KEYS } from "./query-keys";

type QuerySnapshot = {
  queryKey: QueryKey;
  data: unknown;
};

type FeedCacheData =
  | CatalogJobDiscoveryResult
  | InfiniteData<CatalogJobDiscoveryResult>
  | undefined;

const DISCOVER_ROOT = ["job-catalog", "discover"] as const;
const EXPLORE_ROOT = ["job-catalog", "explore"] as const;
const HIDDEN_FEED_SNAPSHOT_ORDER = [
  "job-catalog",
  "hidden-feed-snapshot-order",
] as const;

export function hiddenCatalogJobFeedSnapshotKey(jobId: string) {
  return ["job-catalog", "hidden-feed-snapshot", jobId] as const;
}

function addHiddenSnapshotOrder(queryClient: QueryClient, jobId: string) {
  queryClient.setQueryData<string[]>(HIDDEN_FEED_SNAPSHOT_ORDER, (current) => {
    const order = current ?? [];
    return order.includes(jobId) ? order : [...order, jobId];
  });
}

function removeHiddenSnapshotOrder(
  queryClient: QueryClient,
  jobIds: readonly string[],
) {
  if (jobIds.length === 0) {
    return;
  }

  const removed = new Set(jobIds);
  queryClient.setQueryData<string[]>(HIDDEN_FEED_SNAPSHOT_ORDER, (current) =>
    current ? current.filter((jobId) => !removed.has(jobId)) : current,
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isCatalogJobListItem(value: unknown): value is CatalogJobListItem {
  return isRecord(value) && typeof value.id === "string" && isRecord(value.userState);
}

function updatePageJobs(
  page: CatalogJobDiscoveryResult,
  updater: (jobs: CatalogJobListItem[]) => CatalogJobListItem[],
) {
  const jobs = updater(page.jobs);
  if (jobs === page.jobs) {
    return page;
  }

  const removedCount = Math.max(0, page.jobs.length - jobs.length);
  return {
    ...page,
    jobs,
    total:
      typeof page.total === "number"
        ? Math.max(0, page.total - removedCount)
        : jobs.length,
  };
}

function updateFeedPages(
  data: FeedCacheData,
  updater: (page: CatalogJobDiscoveryResult) => CatalogJobDiscoveryResult,
): FeedCacheData {
  if (!data) {
    return data;
  }

  if (isRecord(data) && Array.isArray(data.pages)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      const nextPage = updater(page as CatalogJobDiscoveryResult);
      changed ||= nextPage !== page;
      return nextPage;
    });
    return changed ? { ...data, pages } : data;
  }

  if (isRecord(data) && Array.isArray(data.jobs)) {
    return updater(data as CatalogJobDiscoveryResult);
  }

  return data;
}

export function removeCatalogJobFromFeedData<T>(data: T, jobId: string): T {
  return removeCatalogJobsFromFeedData(data, [jobId]);
}

export function removeCatalogJobsFromFeedData<T>(
  data: T,
  jobIds: readonly string[],
): T {
  const hidden = new Set(jobIds);
  if (hidden.size === 0) {
    return data;
  }

  return updateFeedPages(data as FeedCacheData, (page) =>
    updatePageJobs(page, (jobs) => {
      const nextJobs = jobs.filter((job) => !hidden.has(job.id));
      return nextJobs.length === jobs.length ? jobs : nextJobs;
    }),
  ) as T;
}

function updateJobUserState(
  job: CatalogJobListItem,
  state: CatalogJobUserStateSummary,
) {
  return {
    ...job,
    userState: {
      ...job.userState,
      ...state,
    },
  };
}

export function updateCatalogJobUserStateInData<T>(
  data: T,
  jobId: string,
  state: CatalogJobUserStateSummary,
): T {
  if (!data) {
    return data;
  }

  if (Array.isArray(data)) {
    let changed = false;
    const items = data.map((item) => {
      if (!isCatalogJobListItem(item) || item.id !== jobId) {
        return item;
      }
      changed = true;
      return updateJobUserState(item, state);
    });
    return (changed ? items : data) as T;
  }

  const recordCandidate = data as unknown;

  if (isRecord(recordCandidate) && Array.isArray(recordCandidate.pages)) {
    return updateFeedPages(recordCandidate as unknown as FeedCacheData, (page) =>
      updatePageJobs(page, (jobs) => {
        let changed = false;
        const nextJobs = jobs.map((job) => {
          if (job.id !== jobId) {
            return job;
          }
          changed = true;
          return updateJobUserState(job, state);
        });
        return changed ? nextJobs : jobs;
      }),
    ) as T;
  }

  if (isRecord(recordCandidate) && Array.isArray(recordCandidate.jobs)) {
    return updateFeedPages(recordCandidate as unknown as FeedCacheData, (page) =>
      updatePageJobs(page, (jobs) => {
        let changed = false;
        const nextJobs = jobs.map((job) => {
          if (job.id !== jobId) {
            return job;
          }
          changed = true;
          return updateJobUserState(job, state);
        });
        return changed ? nextJobs : jobs;
      }),
    ) as T;
  }

  if (isCatalogJobListItem(data) && data.id === jobId) {
    return updateJobUserState(data as CatalogJobListItem, state) as T;
  }

  return data;
}

function getFeedSnapshots(queryClient: QueryClient): QuerySnapshot[] {
  return [
    ...queryClient.getQueriesData({ queryKey: DISCOVER_ROOT }),
    ...queryClient.getQueriesData({ queryKey: EXPLORE_ROOT }),
  ].map(([queryKey, data]) => ({ queryKey, data }));
}

export function restoreQuerySnapshots(
  queryClient: QueryClient,
  snapshots: QuerySnapshot[] | undefined,
) {
  for (const snapshot of snapshots ?? []) {
    queryClient.setQueryData(snapshot.queryKey, snapshot.data);
  }
}

export async function cancelCatalogFeedQueries(queryClient: QueryClient) {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: DISCOVER_ROOT }),
    queryClient.cancelQueries({ queryKey: EXPLORE_ROOT }),
  ]);
}

export function removeCatalogJobFromFeedCaches(
  queryClient: QueryClient,
  jobId: string,
) {
  return removeCatalogJobsFromFeedCaches(queryClient, [jobId]);
}

export function removeCatalogJobsFromFeedCaches(
  queryClient: QueryClient,
  jobIds: readonly string[],
) {
  const uniqueJobIds = [...new Set(jobIds)];
  const snapshots = getFeedSnapshots(queryClient);

  for (const jobId of uniqueJobIds) {
    queryClient.setQueryData(
      hiddenCatalogJobFeedSnapshotKey(jobId),
      snapshots,
    );
    addHiddenSnapshotOrder(queryClient, jobId);
  }

  queryClient.setQueriesData({ queryKey: DISCOVER_ROOT }, (data) =>
    removeCatalogJobsFromFeedData(data, uniqueJobIds),
  );
  queryClient.setQueriesData({ queryKey: EXPLORE_ROOT }, (data) =>
    removeCatalogJobsFromFeedData(data, uniqueJobIds),
  );

  return snapshots;
}

export function restoreHiddenCatalogJobFeedCaches(
  queryClient: QueryClient,
  jobId: string,
) {
  const snapshotKey = hiddenCatalogJobFeedSnapshotKey(jobId);
  const hiddenSnapshots =
    queryClient.getQueryData<QuerySnapshot[]>(snapshotKey) ?? [];
  const currentSnapshots = getFeedSnapshots(queryClient);

  restoreQuerySnapshots(queryClient, hiddenSnapshots);

  return currentSnapshots;
}

export function restoreHiddenCatalogJobsFeedCaches(
  queryClient: QueryClient,
  jobIds: readonly string[],
) {
  const currentSnapshots = getFeedSnapshots(queryClient);
  const pending = new Set(jobIds);
  const snapshotOrder =
    queryClient.getQueryData<string[]>(HIDDEN_FEED_SNAPSHOT_ORDER) ?? [];
  const orderedJobIds = [
    ...snapshotOrder.filter((jobId) => pending.has(jobId)),
    ...jobIds.filter((jobId) => !snapshotOrder.includes(jobId)),
  ];
  const snapshotJobId = orderedJobIds.find((jobId) =>
    queryClient.getQueryData(hiddenCatalogJobFeedSnapshotKey(jobId)),
  );
  const hiddenSnapshots = snapshotJobId
    ? queryClient.getQueryData<QuerySnapshot[]>(
        hiddenCatalogJobFeedSnapshotKey(snapshotJobId),
      )
    : [];

  restoreQuerySnapshots(queryClient, hiddenSnapshots);

  return currentSnapshots;
}

export function clearHiddenCatalogJobFeedSnapshot(
  queryClient: QueryClient,
  jobId: string,
) {
  queryClient.removeQueries({
    queryKey: hiddenCatalogJobFeedSnapshotKey(jobId),
    exact: true,
  });
  removeHiddenSnapshotOrder(queryClient, [jobId]);
}

export function clearHiddenCatalogJobFeedSnapshots(
  queryClient: QueryClient,
  jobIds: readonly string[],
) {
  for (const jobId of jobIds) {
    clearHiddenCatalogJobFeedSnapshot(queryClient, jobId);
  }
}

export function updateCatalogJobUserStateCaches(
  queryClient: QueryClient,
  jobId: string,
  state: CatalogJobUserStateSummary,
) {
  queryClient.setQueriesData({ queryKey: DISCOVER_ROOT }, (data) =>
    updateCatalogJobUserStateInData(data, jobId, state),
  );
  queryClient.setQueriesData({ queryKey: EXPLORE_ROOT }, (data) =>
    updateCatalogJobUserStateInData(data, jobId, state),
  );
  queryClient.setQueriesData({ queryKey: ["job-catalog", "saved"] }, (data) =>
    updateCatalogJobUserStateInData(data, jobId, state),
  );
  queryClient.setQueryData(QUERY_KEYS.JOB_CATALOG_DETAIL(jobId), (data) =>
    updateCatalogJobUserStateInData(data, jobId, state),
  );
}

export function invalidateCatalogJobPeripheralState(
  queryClient: QueryClient,
  jobId?: string | null,
) {
  queryClient.invalidateQueries({
    queryKey: ["job-catalog", "saved"],
    refetchType: "inactive",
  });
  queryClient.invalidateQueries({
    queryKey: ["job-catalog", "history"],
    refetchType: "inactive",
  });
  if (jobId) {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.JOB_CATALOG_DETAIL(jobId),
      refetchType: "inactive",
    });
  }
}
